<template>
  <v-card id="module-completion" dark flat class="completion-card" :class="{ 'is-completed': isCompleted }">
    <header class="completion-header">
      <span class="completion-icon">
        <v-icon>{{ isCompleted ? "mdi-check-decagram-outline" : "mdi-party-popper" }}</v-icon>
      </span>
      <div class="completion-heading">
        <div class="completion-eyebrow">Dein Modulabschluss</div>
        <h2>{{ isCompleted ? "Modul abgeschlossen" : "Bereit für den letzten Klick?" }}</h2>
        <p>{{ headerText }}</p>
      </div>
      <span v-if="isCompleted" class="completion-status"><v-icon>mdi-check</v-icon>Erledigt</span>
    </header>

    <div v-if="isCompleted" class="completion-success">
      <v-icon>mdi-check-circle-outline</v-icon>
      <div>
        <strong>Gelesen, verstanden und abgeschlossen.</strong>
        <span>Dein Lernfortschritt wurde gespeichert.</span>
      </div>
    </div>

    <div v-else-if="!lessonsComplete" class="completion-locked">
      <v-icon>mdi-lock-outline</v-icon>
      <div>
        <strong>Noch nicht ganz am Ziel</strong>
        <span>Schliesse zuerst alle Lektionen dieses Moduls ab.</span>
      </div>
    </div>

    <div v-else class="completion-action-wrap">
      <div class="completion-action-copy">
        <span><v-icon>mdi-party-popper</v-icon>Fast geschafft</span>
        <strong>Ein Klick bis zum Ziel</strong>
        <p>Du hast alle Inhalte bearbeitet. Jetzt kannst du das Modul offiziell abschliessen.</p>
      </div>

      <button type="button" class="completion-action" :disabled="saving" @click="completeModule">
        <v-progress-circular v-if="saving" indeterminate color="white" size="22" width="3" />
        <template v-else>
          <v-icon>mdi-party-popper</v-icon>
          <span>Gelesen, verstanden, einverstanden</span>
          <v-icon>mdi-check-bold</v-icon>
        </template>
      </button>

      <p class="completion-note"><v-icon>mdi-information-outline</v-icon>Damit bestätigst du, dass du alle Inhalte gelesen und verstanden hast.</p>
      <v-alert v-if="error" type="error" text outlined class="completion-error">{{ error }}</v-alert>
    </div>
  </v-card>
</template>

<script>
import api from "@/api"

export default {
  name: "ModuleCompletionAction",
  props: {
    moduleId: { type: String, required: true },
    progress: { type: Object, default: null },
    lessonsComplete: { type: Boolean, default: false },
  },
  data() {
    return {
      saving: false,
      error: "",
    }
  },
  computed: {
    isCompleted() {
      return Boolean(this.progress && this.progress.completedAt)
    },
    headerText() {
      return this.isCompleted
        ? "Alle notwendigen Schritte sind erledigt."
        : "Du hast alle Lektionen geschafft. Mit deiner Bestätigung schliesst du das Modul ab."
    },
  },
  methods: {
    async completeModule() {
      if (this.saving || !this.lessonsComplete || this.isCompleted) return
      this.saving = true
      this.error = ""
      try {
        const result = await api.akademie.acknowledgeModule(this.moduleId)
        this.$emit("completed", result && result.progress ? result.progress : null)
      } catch (error) {
        this.error = (error.response && error.response.data && error.response.data.error) || "Das Modul konnte nicht abgeschlossen werden."
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.completion-card {
  margin-top: 1rem;
  overflow: hidden;
  border: 1px solid rgba(88, 211, 136, 0.26);
  border-radius: 1.25rem;
  color: #f4f4f5;
  background: radial-gradient(circle at 92% 0%, rgba(88, 211, 136, 0.14), transparent 22rem), rgba(255, 255, 255, 0.035) !important;
  font-size: 1rem;
}

.completion-card.is-completed {
  border-color: rgba(88, 211, 136, 0.32);
  background: radial-gradient(circle at 92% 0%, rgba(0, 150, 64, 0.15), transparent 22rem), rgba(255, 255, 255, 0.035) !important;
}

.completion-header {
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;
  padding: 1.125rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.09);
}

.completion-icon {
  display: grid;
  width: 2.75rem;
  min-width: 2.75rem;
  height: 2.75rem;
  place-items: center;
  border-radius: 0.8125rem;
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.14);
}

.completion-icon .v-icon,
.completion-success > .v-icon,
.completion-locked > .v-icon {
  color: currentColor;
}

.completion-heading {
  min-width: 0;
  flex: 1 1 auto;
}

.completion-eyebrow,
.completion-action-copy > span {
  color: #7ee5a6;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.completion-header h2 {
  margin: 0.125rem 0;
  color: #fff;
  font-size: 1.25rem;
  line-height: 1.3;
}

.completion-header p {
  margin: 0;
  color: #a3a3a8;
  font-size: 0.875rem;
  line-height: 1.5;
}

.completion-status {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.375rem 0.625rem;
  border-radius: 999px;
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.13);
  font-size: 0.75rem;
  font-weight: 800;
  white-space: nowrap;
}

.completion-status .v-icon {
  color: currentColor;
  font-size: 1rem;
}

.completion-success,
.completion-locked {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin: 1.125rem;
  padding: 0.875rem;
  border: 1px solid rgba(88, 211, 136, 0.2);
  border-radius: 0.875rem;
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.08);
}

.completion-locked {
  border-color: rgba(96, 165, 250, 0.2);
  color: #93c5fd;
  background: rgba(96, 165, 250, 0.07);
}

.completion-success strong,
.completion-success span,
.completion-locked strong,
.completion-locked span {
  display: block;
}

.completion-success strong,
.completion-locked strong {
  color: #f4f4f5;
  font-size: 1rem;
}

.completion-success span,
.completion-locked span {
  margin-top: 0.125rem;
  color: #c7c7cb;
  font-size: 0.875rem;
  line-height: 1.5;
}

.completion-action-wrap {
  display: grid;
  gap: 1rem;
  padding: 1.125rem;
}

.completion-action-copy {
  padding: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 0.875rem;
  background: rgba(0, 0, 0, 0.13);
}

.completion-action-copy > span {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
}

.completion-action-copy > span .v-icon {
  color: currentColor;
  font-size: 1rem;
}

.completion-action-copy strong {
  display: block;
  margin-top: 0.375rem;
  color: #fff;
  font-size: 1.125rem;
  line-height: 1.35;
}

.completion-action-copy p {
  margin: 0.25rem 0 0;
  color: #c7c7cb;
  font-size: 0.875rem;
  line-height: 1.5;
}

.completion-action {
  display: flex;
  width: 100%;
  min-height: 4rem;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 0.875rem 1.25rem;
  border: 1px solid rgba(126, 229, 166, 0.5);
  border-radius: 1rem;
  color: #fff;
  background: linear-gradient(120deg, #007d37, #009640 52%, #14a968);
  box-shadow: 0 0.75rem 2rem rgba(0, 150, 64, 0.22);
  cursor: pointer;
  font: inherit;
  font-size: 1rem;
  font-weight: 800;
  letter-spacing: 0;
  line-height: 1.35;
  text-align: center;
}

.completion-action:hover {
  border-color: rgba(255, 255, 255, 0.72);
  background: linear-gradient(120deg, #008f3f, #08a34a 52%, #1ab874);
  box-shadow: 0 0.875rem 2.25rem rgba(0, 150, 64, 0.3);
}

.completion-action:focus-visible {
  outline: 2px solid #fff;
  outline-offset: 3px;
}

.completion-action:disabled {
  cursor: wait;
  opacity: 0.72;
}

.completion-action .v-icon {
  color: currentColor;
  font-size: 1.375rem;
}

.completion-note {
  display: inline-flex;
  align-items: flex-start;
  gap: 0.375rem;
  margin: 0;
  color: #a3a3a8;
  font-size: 0.875rem;
  line-height: 1.5;
}

.completion-note .v-icon {
  margin-top: 0.0625rem;
  color: #7ee5a6;
  font-size: 1rem;
}

.completion-error {
  margin: 0;
  font-size: 0.875rem;
}

@media (max-width: 37.49rem) {
  .completion-header {
    display: grid;
    grid-template-columns: 2.75rem minmax(0, 1fr);
  }

  .completion-status {
    grid-column: 2;
    justify-self: start;
  }

  .completion-action {
    min-height: 4.5rem;
    flex-wrap: wrap;
  }

  .completion-action span {
    flex: 1 1 12rem;
  }
}
</style>
